import { createAsyncThunk } from '@reduxjs/toolkit';
import { MenuOption, MenuOptionGroup, MenuOptionMenuOptionGroup } from './types';
import { createApiRequest } from '../../utils/apiUtils';

const getToken = () => localStorage.getItem('accessToken');
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const addMenuOption = createAsyncThunk<
  MenuOption,
  {
    storeId: string;
    name: string;
    price: number | null;
    priceType: string | null;
    sortIndex: number;
  }
>(
  'menuOption/addMenuOption',
  async ({ storeId, name, price, priceType, sortIndex }, { rejectWithValue }) => {
    try {
      return await createApiRequest<MenuOption>(`/stores/${storeId}/menu-options`, {
        method: 'POST',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, price, priceType, sortIndex }),
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const fetchMenuOptions = createAsyncThunk<MenuOption[], string>(
  'menuOption/fetchMenuOptions',
  async (storeId, { rejectWithValue }) => {
    try {
      return await createApiRequest<MenuOption[]>(`/stores/${storeId}/menu-options`, {
        method: 'GET',
        requiresAuth: true,
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const fetchMenuOptionGroups = createAsyncThunk<
  MenuOptionGroup[],
  { storeId: string; menuId: string }
>(
  'menuOption/fetchMenuOptionGroups',
  async ({ storeId, menuId }, { rejectWithValue }) => {
    try {
      return await createApiRequest<MenuOptionGroup[]>(`/stores/${storeId}/menu-options/groups`, {
        method: 'GET',
        requiresAuth: true,
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const addMenuOptionToGroupApi = createAsyncThunk<
  MenuOptionMenuOptionGroup[],
  {
    storeId: string;
    groupId: string;
    optionId: string;
    sortIndex: number;
  }
>(
  'menuOption/addMenuOptionToGroupApi',
  async ({ storeId, groupId, optionId, sortIndex }, { rejectWithValue }) => {
    try {
      await createApiRequest(
        `/stores/${storeId}/menu-options/groups/${groupId}/options/${optionId}`,
        {
          method: 'POST',
          requiresAuth: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sortIndex }),
        }
      );
      // 紐付け後に最新の紐付け情報を取得
      return await createApiRequest<MenuOptionMenuOptionGroup[]>(
        `/stores/${storeId}/menu-options/groups/${groupId}/options`,
        {
          method: 'GET',
          requiresAuth: true,
        }
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const removeMenuOptionFromGroupApi = createAsyncThunk<
  MenuOptionMenuOptionGroup[],
  {
    storeId: string;
    groupId: string;
    optionId: string;
  }
>(
  'menuOption/removeMenuOptionFromGroupApi',
  async ({ storeId, groupId, optionId }, { rejectWithValue }) => {
    try {
      await createApiRequest(
        `/stores/${storeId}/menu-options/groups/${groupId}/options/${optionId}`,
        {
          method: 'DELETE',
          requiresAuth: true,
        }
      );
      // 解除後に最新の紐付け情報を取得
      return await createApiRequest<MenuOptionMenuOptionGroup[]>(
        `/stores/${storeId}/menu-options/groups/${groupId}/options`,
        {
          method: 'GET',
          requiresAuth: true,
        }
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const addMenuOptionGroupApi = createAsyncThunk<
  MenuOptionGroup,
  {
    storeId: string;
    name: string;
    sortIndex: number;
    /** 必須グループかどうか（省略時は BE 側で true 扱い） */
    required?: boolean;
  }
>(
  'menuOption/addMenuOptionGroupApi',
  async ({ storeId, name, sortIndex, required }, { rejectWithValue }) => {
    try {
      return await createApiRequest<MenuOptionGroup>(`/stores/${storeId}/menu-options/groups`, {
        method: 'POST',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, sortIndex, required }),
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// メニューオプショングループの更新（必須/任意の切替など。null/undefined のフィールドは現状維持）
export const updateMenuOptionGroupApi = createAsyncThunk<
  MenuOptionGroup,
  {
    storeId: string;
    id: string;
    name?: string;
    sortIndex?: number;
    required?: boolean;
  }
>(
  'menuOption/updateMenuOptionGroupApi',
  async ({ storeId, id, name, sortIndex, required }, { rejectWithValue }) => {
    try {
      return await createApiRequest<MenuOptionGroup>(`/stores/${storeId}/menu-options/groups/${id}`, {
        method: 'PUT',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, sortIndex, required }),
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const deleteMenuOptionGroupApi = createAsyncThunk<
  void,
  {
    storeId: string;
    id: string;
  }
>(
  'menuOption/deleteMenuOptionGroupApi',
  async ({ storeId, id }, { rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/menu-options/groups/${id}`, {
        method: 'DELETE',
        requiresAuth: true,
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const deleteMenuOptionApi = createAsyncThunk<
  void,
  {
    storeId: string;
    id: string;
  }
>(
  'menuOption/deleteMenuOptionApi',
  async ({ storeId, id }, { rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/menu-options/${id}`, {
        method: 'DELETE',
        requiresAuth: true,
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// 単一のメニューオプショングループを取得するエンドポイントを追加
export const fetchMenuOptionGroup = createAsyncThunk<
  MenuOptionGroup,
  {
    storeId: string;
    groupId: string;
  }
>(
  'menuOption/fetchMenuOptionGroup',
  async ({ storeId, groupId }, { rejectWithValue }) => {
    try {
      return await createApiRequest<MenuOptionGroup>(`/stores/${storeId}/menu-options/groups/${groupId}`, {
        method: 'GET',
        requiresAuth: true,
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const fetchMenuOptionMenuOptionGroup = createAsyncThunk<
  MenuOptionMenuOptionGroup[],
  { storeId: string; groupId: string;}
>(
  'menuOption/fetchMenuOptionMenuOptionGroup',
  async ({ storeId, groupId }, { rejectWithValue }) => {
    try {
      return await createApiRequest<MenuOptionMenuOptionGroup[]>(
        `/stores/${storeId}/menu-options/groups/${groupId}/options`,
        {
          method: 'GET',
          requiresAuth: true,
        }
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);
